import React from "react";
import {
	DndContext,
	closestCenter,
	PointerSensor,
	useSensor,
	useSensors,
	KeyboardSensor,
} from "@dnd-kit/core";
import {
	SortableContext,
	arrayMove,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
	useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export function SortableItem({ id, children, disabled = false }) {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id, disabled });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.8 : 1,
		cursor: disabled ? "default" : isDragging ? "grabbing" : "grab",
	};

	return (
		<div ref={setNodeRef} style={style} {...attributes} {...listeners}>
			{children}
		</div>
	);
}

export default function SortableList({
	items,
	getId,
	onReorder,
	children,
	axis = "y",
	disabled = false,
}) {
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: { distance: 5 },
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	const ids = items.map((item, index) => getId?.(item, index) ?? index.toString());

	const handleDragEnd = (event) => {
		const { active, over } = event;
		if (!over || active.id === over.id || disabled) return;

		const oldIndex = ids.indexOf(active.id);
		const newIndex = ids.indexOf(over.id);
		if (oldIndex === -1 || newIndex === -1) return;

		const reordered = arrayMove(items, oldIndex, newIndex);
		onReorder?.(reordered, { oldIndex, newIndex });
	};

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={closestCenter}
			onDragEnd={handleDragEnd}
		>
			<SortableContext
				items={ids}
				strategy={verticalListSortingStrategy}
			>
				{children(SortableItem, ids)}
			</SortableContext>
		</DndContext>
	);
}

